import { ApplicationController, raw } from '@aha-app/mvc';
import type WhiteboardController from '../controllers/WhiteboardController';
import type {
  CreateParent as SmartRecordParent,
  SmartRecordCreatableType,
} from '../types';
import type Canvas from '../objects/Canvas';
import type { IRecordManager } from '../shared-types';
import { canConvertSmartRecords, isShapeSmartRecord } from '../objects/guards';
import { pluralize } from '@aha-app/aha-shared';
import SmartRecordDataManager from '../SmartRecords/SmartRecordDataManager';
import SmartRecordConverter from '../SmartRecords/SmartRecordConverter';
import type ShapeDrawingBrush from '../stylus/ShapeDrawingBrush';
import SimpleSelection from 'fabric/SimpleSelection';
import { sortShapesLTR } from '../utils/shapes';
import type ShapeSmartRecord from '../shapes/ShapeSmartRecord';
import type Shape from '../objects/Shape';
import { getConnectorAttachmentObjects } from '../utils/connectors';

interface State {
  dataManager: SmartRecordDataManager;
  recordConverter: SmartRecordConverter;
  submitting: boolean;
  smartRecordsPresent: boolean;
  showResults: boolean;
  // Looking up the records may take some time, so show another intermediary screen
  // while that is happening
  resultsProcessing: boolean;
  panelVisible: boolean;
  convertingRecordType: string;
  useConnectorHierarchies: boolean;
}

// Most of the functionality in this controller has to do with the SmartRecordsPanel
// side panel item. The main tasks center around finding the set of entries/records
// for a given recordType. This is made easier with helpers, with the tradeoff
// that we are calling array operations many times in casese where we could store
// a reference to the array and pass that through.
class SmartRecordsController extends ApplicationController<State> {
  canvas: Canvas;
  whiteboardController: WhiteboardController;
  recordManager?: IRecordManager;
  // Cleanup func to remove canvas listeners
  cleanup?: () => void;

  startController(canvas: Canvas, whiteboardController: WhiteboardController) {
    this.canvas = canvas;
    this.whiteboardController = whiteboardController;
    this.recordManager = whiteboardController.recordManager;

    this.actionInitializeState();

    canvas.on('object:added', this.actionSetSmartRecordsPresent);
    canvas.on('object:removed', this.actionSetSmartRecordsPresent);
    canvas.on('object:changed', this.actionSetSmartRecordsPresent);

    canvas.on('selection:created', this.actionUpdateSelectedEntries);
    canvas.on('selection:updated', this.actionUpdateSelectedEntries);

    this.cleanup = () => {
      canvas.off('object:added', this.actionSetSmartRecordsPresent);
      canvas.off('object:removed', this.actionSetSmartRecordsPresent);
      canvas.off('object:changed', this.actionSetSmartRecordsPresent);
      canvas.off('selection:created', this.actionUpdateSelectedEntries);
      canvas.off('selection:updated', this.actionUpdateSelectedEntries);
    };
  }

  destroy() {
    this.cleanup?.();
  }

  get availableRecordTypes() {
    return this.state.dataManager.availableRecordTypes;
  }

  get readyToSubmit() {
    return this.state.dataManager.readyToSubmit;
  }

  get allRecordsSelected() {
    return Object.values(this.state.dataManager.selections).every(Boolean);
  }

  get recordCount() {
    return this.state.dataManager.allRecords.length;
  }

  get recordSelectedCount() {
    return this.state.dataManager.recordSelectedCount;
  }

  get showPromo() {
    return (
      !canConvertSmartRecords() &&
      window.currentAccount?.hasCrossProductPromotionsEnabled
    );
  }

  get useConnectorHierarchies() {
    return this.state.useConnectorHierarchies;
  }

  hasValidConnectorHierarchies() {
    const selectedRecords = this.state.dataManager.allRecords.filter(record =>
      this.state.dataManager.recordSelected(record)
    );
    return selectedRecords.some(record => {
      const attachments = getConnectorAttachmentObjects(record);
      return attachments.some(
        attachment =>
          this.state.dataManager.recordSelected(
            attachment as Shape<ShapeSmartRecord>
          ) &&
          this.state.dataManager.isValidRelation(
            record.recordType as SmartRecordCreatableType,
            attachment.recordType as SmartRecordCreatableType
          )
      );
    });
  }

  userSelectedFieldsForRecordType(recordType: SmartRecordCreatableType) {
    return this.state.dataManager.userSelectedFieldsForRecordType(recordType);
  }

  fieldsForRecordType(recordType: SmartRecordCreatableType) {
    return this.state.dataManager.fields[recordType];
  }

  recordsForTypeAsArray(recordType: SmartRecordCreatableType) {
    return this.state.dataManager.recordsForTypeAsArray(recordType);
  }

  // This currently only updates on refresh, not dynamically.
  orderRecordsForTypeAsArray(recordType: SmartRecordCreatableType) {
    return sortShapesLTR(this.recordsForTypeAsArray(recordType));
  }

  recordSelected(shape: Shape<ShapeSmartRecord>) {
    return this.state.dataManager.recordSelected(shape);
  }

  actionInitializeState() {
    this.state.dataManager = new SmartRecordDataManager(this);
    this.state.recordConverter = new SmartRecordConverter(
      this.state.dataManager,
      this.recordManager,
      this.whiteboardController.fetchRecordInfo.bind(this.whiteboardController)
    );
    this.resetConversionProgressState();
    this.setShowResults(false);
    this.setResultsProcessing(false);
    this.state.smartRecordsPresent = false;
    this.state.panelVisible = false;
    this.state.useConnectorHierarchies = true;
  }

  actionSetPanelVisible(visible: boolean) {
    this.state.panelVisible = visible;
  }

  actionSetSmartRecordsPresent() {
    this.state.smartRecordsPresent =
      this.canvas.getObjects().filter(isShapeSmartRecord).length > 0;
  }

  actionInitializePanel(trySelectAll = false) {
    this.actionResetState();
    this.actionGetSmartRecords(trySelectAll);
  }

  actionGetSmartRecords(trySelectAll = false) {
    const shapes = this.canvas.getObjects().filter(isShapeSmartRecord);
    this.state.dataManager.setRecords(shapes, trySelectAll);
    this.actionSelectActiveSmartShapes();
    this.updateCanvasSelection();
  }

  actionSetProject(
    recordType: SmartRecordCreatableType,
    parent: SmartRecordParent
  ) {
    this.state.dataManager.setProject(recordType, parent);
  }

  actionSetRelease(
    recordType: SmartRecordCreatableType,
    parent: SmartRecordParent
  ) {
    this.state.dataManager.setRelease(recordType, parent);
  }

  actionSetFeature(
    recordType: SmartRecordCreatableType,
    parent: SmartRecordParent
  ) {
    this.state.dataManager.setFeature(recordType, parent);
  }

  // Generate the records in Aha!.
  async actionCreateRecords() {
    if (!this.state.dataManager.readyToSubmit) return;
    if (this.state.submitting) return;

    this.state.submitting = true;

    await this.state.recordConverter.convertRecords(
      this.actionUpdateConvertingRecord
    );

    this.resetConversionProgressState();

    await this.processResults();

    // Handle messaging and show results
    this.postCreationActions();
  }

  actionSetSelected(id: RecordId, selected: boolean) {
    this.state.dataManager.setSelected(id, selected);
    this.updateCanvasSelection();
  }

  actionToggleSelectAll(selected: boolean) {
    this.state.dataManager.toggleSelectAll(selected);
    this.updateCanvasSelection();
  }

  // Goes from results page to create page in side panel. Slightly different than
  // actionInitializePanel because we also reset the fields here
  actionResetPanel() {
    this.actionResetState();
    this.state.dataManager.resetFields();
    this.actionGetSmartRecords();
  }

  actionAddSmartRecord() {
    const featureStylus = this.whiteboardController.smartRecordStyluses.find(
      stylus =>
        stylus.shapeName === 'smartRecord' &&
        stylus.options.recordType === 'features'
    );
    if (!featureStylus) {
      throw new Error('Stylus not found for feature smart record');
    }

    const center = this.whiteboardController.canvas.getCenter();
    const zoom = this.whiteboardController.canvas.getZoom();
    const viewportTransform =
      this.whiteboardController.canvas.viewportTransform;

    // Get current center of the viewport
    const adjustedCenter = {
      left: (center.left - (viewportTransform?.[4] ?? 0)) / zoom,
      top: (center.top - (viewportTransform?.[5] ?? 0)) / zoom,
    };
    featureStylus?.activate();
    (
      this.whiteboardController.canvas.freeDrawingBrush as ShapeDrawingBrush
    ).createShape({
      drawing: false,
      options: { left: adjustedCenter.left, top: adjustedCenter.top },
    });

    this.whiteboardController.stylusPointer.activate();
    this.whiteboardController.actionHideConnectorShapeMenu();
    this.whiteboardController.actionShowObjectToolbar();
    this.whiteboardController.canvas.requestRenderAll();
  }

  actionGetParent(record: Shape<ShapeSmartRecord>) {
    return this.state.dataManager.getParent(record);
  }

  actionGetRecordFields(record: Shape<ShapeSmartRecord>) {
    return this.state.dataManager.getRecordFields(record);
  }

  actionGetConvertedRecordTypes() {
    return this.state.recordConverter.convertedRecordTypes;
  }

  actionToggleUseConnectorHierarchies(isChecked: boolean) {
    this.state.useConnectorHierarchies = isChecked;
  }

  showTeams(recordType: SmartRecordCreatableType) {
    return ['releases', 'epics', 'features', 'requirements'].includes(
      recordType
    );
  }

  private actionUpdateSelectedEntries() {
    if (!this.state.panelVisible) return;
    this.actionSelectActiveSmartShapes();
  }

  // Syncs the currently selected objects with the selected entries in the side
  // panel. If no objects are selected on open, then all entries are selected
  private actionSelectActiveSmartShapes() {
    const smartShapes = this.canvas
      .getActiveObjects()
      .filter(isShapeSmartRecord);

    if (smartShapes.length > 0) {
      this.state.dataManager.toggleSelectAll(false);
      smartShapes.forEach(shape => {
        const recordType = shape.recordType!;
        const record = this.state.dataManager.records[recordType][shape.id];
        if (record) {
          this.state.dataManager.setSelected(record.id, true);
        }
      });
    }
  }

  // Syncs the selected entries in the side panel with the current selection in
  // the canvas
  private updateCanvasSelection() {
    const selectedRecords = raw(this.state.dataManager.allRecords).filter(
      record => this.state.dataManager.recordSelected(record)
    );

    if (selectedRecords.length > 0) {
      this.canvas.replaceSelectionWith(
        new SimpleSelection(selectedRecords, { canvas: this.canvas })
      );
    } else if (
      this.canvas.getActiveObject()?.getObjects().filter(isShapeSmartRecord)
        .length > 0
    ) {
      // discard active object only if there are smart shapes selected
      this.canvas.discardActiveObject();
      this.canvas.requestRenderAll();
    }
  }

  private setShowResults(show: boolean) {
    this.state.showResults = show;
  }

  private setResultsProcessing(processing: boolean) {
    this.state.resultsProcessing = processing;
  }

  private actionResetState() {
    this.state.dataManager.resetRecords();
    this.state.recordConverter.reset();
    this.resetConversionProgressState();
    this.setShowResults(false);
    this.setResultsProcessing(false);
  }

  private convertSmartRecordShapes(recordType: SmartRecordCreatableType) {
    const objects = this.canvas.getObjects().filter(isShapeSmartRecord);
    this.state.recordConverter.resultsByType[recordType].forEach(result => {
      const obj = objects.find(obj => obj.id === result.id);
      if (obj) {
        const width = obj.getScaledWidth();
        const height = obj.getScaledHeight();
        obj.set({
          recordId: result.recordId,
          shapeName: 'record',
          scaleX: 1,
          scaleY: 1,
          width,
          height,
        });
      }
    });
  }

  private actionShowResultsMessage() {
    const { results } = this.state.recordConverter;
    this.whiteboardController.actionShowMessage(
      results.length > 0
        ? `${results.length} new ${pluralize(results.length, 'record')} ${
            results.length > 1 ? 'were' : 'was'
          } created.`
        : 'No Aha! shapes were converted. Please see the results for more information.'
    );
  }

  private readonly actionUpdateConvertingRecord = (
    recordType: SmartRecordCreatableType | ''
  ) => {
    // This record type just finished converting to aha records, so update the
    // smart records for this type in the whiteboard to record cards
    if (this.state.convertingRecordType) {
      this.convertSmartRecordShapes(
        this.state.convertingRecordType as SmartRecordCreatableType
      );
    }
    this.state.convertingRecordType = recordType;
  };

  private resetConversionProgressState() {
    // This will take us to the results screen, which will show info regarding
    // records that may have failed to convert
    this.actionUpdateConvertingRecord('');
    this.state.submitting = false;
  }

  private async processResults() {
    this.setShowResults(true);
    this.setResultsProcessing(true);
    await this.state.recordConverter.processConversionResults();
    this.setResultsProcessing(false);
  }

  private postCreationActions() {
    this.actionShowResultsMessage();
  }
}

export default SmartRecordsController;
